import type { UserRole } from '@/types/auth'
import {
  ROLE_PRECEDENCE_INDEX,
  ROLE_PRECEDENCE,
  STAFF_APP_ROLES,
} from './roleSets'

export { ROLE_PRECEDENCE, STAFF_APP_ROLES }

export function primaryRoleFromRoles(roles: readonly UserRole[]): UserRole {
  if (roles.length === 0) return 'Patient'
  let best = roles[0]
  let bestIdx = ROLE_PRECEDENCE_INDEX[best]
  for (let i = 1; i < roles.length; i++) {
    const r = roles[i]
    const idx = ROLE_PRECEDENCE_INDEX[r]
    if (idx < bestIdx) {
      best = r
      bestIdx = idx
    }
  }
  return best
}

export function userHasRole(roles: readonly UserRole[], role: UserRole): boolean {
  return roles.includes(role)
}

export function userHasStaffAppRole(roles: readonly UserRole[]): boolean {
  const staffSet: ReadonlySet<UserRole> = new Set(STAFF_APP_ROLES)
  return roles.some((r) => staffSet.has(r))
}

export function isPatientOnlyProfile(roles: readonly UserRole[]): boolean {
  return roles.length === 1 && roles[0] === 'Patient'
}

/** Dedupe while preserving first-seen order. */
export function normalizeRolesList(roles: readonly UserRole[]): UserRole[] {
  const seen = new Set<UserRole>()
  const out: UserRole[] = []
  for (const r of roles) {
    if (!seen.has(r)) {
      seen.add(r)
      out.push(r)
    }
  }
  return out
}

/** Nine distinct Chakra colorSchemes — no duplicates, no red. */
const ROLE_COLOR_SCHEME: Record<UserRole, string> = {
  SystemAdmin: 'purple',
  Management: 'blue',
  Doctor: 'teal',
  Ophthalmologist: 'pink',
  Optometrist: 'cyan',
  Nurse: 'green',
  Dispensary: 'orange',
  Accountant: 'linkedin',
  Patient: 'gray',
}

/** Chakra colorScheme for a role badge/chip; matches across header and user-management UI. */
export function roleColorScheme(role: UserRole): string {
  return ROLE_COLOR_SCHEME[role] ?? 'gray'
}

const KNOWN_ROLE = new Set<string>(ROLE_PRECEDENCE)

/** Normalize DB/client `roles` payloads into a valid non-empty role list. */
export function coerceUserRoles(raw: unknown): UserRole[] {
  const parts = Array.isArray(raw)
    ? raw.filter((x): x is string => typeof x === 'string')
    : []
  const valid = parts.filter((x): x is UserRole => KNOWN_ROLE.has(x))
  if (valid.length === 0) return ['Patient']
  return normalizeRolesList(valid)
}
